import {
    BackendError,
    CustomError,
    InputError,
    OverbookingError,
    SearchError,
    UserInputError,
    ZeroCapacityError,
} from './types/custom-errors';
import {
    ExchangeRate,
    FeatureFlags,
    Line,
    LineRidesSearchResult,
    RideDetails,
    RideSearchResult,
    RideSummary,
} from './api-interfaces';
import { transformRideData, transformRideKPIData as transformRideSummaryData } from './utils/data-transform.utils';

import React from 'react';
import { Temporal } from '@js-temporal/polyfill';
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';

export interface ApiConfig {
    azureAdApiId: string;
    ridehubApiUrl: string;
    blankRedirectUri: string;
}

const ApiConfigContext = React.createContext<ApiConfig | null>(null);

export function ApiContextProvider(props: { apiConfig: ApiConfig; children: React.ReactNode }) {
    if (!props.apiConfig) {
        return null;
    }
    return <ApiConfigContext.Provider value={props.apiConfig}>{props.children}</ApiConfigContext.Provider>;
}

export class OutsideContextError extends Error {}

export function useApiConfig() {
    const context = React.useContext(ApiConfigContext);
    if (!context) {
        throw new OutsideContextError('useApiConfig must be used within a ApiContextProvider');
    }
    return context as ApiConfig;
}

function useAuthToken() {
    const { instance, accounts } = useMsal();
    const apiConfig = useApiConfig();
    return async () => {
        await instance.initialize();
        const RIDES_API_SCOPE = `${apiConfig.azureAdApiId}/OAuth.Rides.Read`;
        const authResult = await instance.acquireTokenSilent({
            account: accounts[0],
            scopes: [RIDES_API_SCOPE],
            redirectUri: apiConfig.blankRedirectUri,
        });
        return authResult.accessToken;
    };
}

function buildAuthFetch(
    queryUrl: string,
    getAccessToken: () => Promise<string>,
    httpMethod?: 'GET' | 'POST',
    body?: object
) {
    return async (): Promise<Response> => {
        const accessToken = await getAccessToken();
        const authHeader = `Bearer ${accessToken}`;
        const headers = new Headers();
        headers.append('Authorization', authHeader);
        headers.append('Content-Type', 'application/json');
        const options: RequestInit = {
            method: httpMethod ? httpMethod : 'GET',
            headers: headers,
        };
        if (body) {
            options.body = JSON.stringify(body);
        }
        return fetch(queryUrl, options);
    };
}

/**
 * @param auth - Hack to disable obtaining a token while testing queries
 * TODO: Test the authentication logic as part of
 * https://jira.flix.tech/browse/OPT-1368
 * e.g. with https://github.com/Mimetis/msal-react-tester
 * @returns A fetch function that parses a JSON response and optionally transformed it
 */
function useHttpGet(queryUrl: string, transformFn?: (param: unknown) => unknown, auth = true) {
    const getTokenFunc = useAuthToken();
    function parseAndTransform(res: Response) {
        if (res.ok) {
            return res.json().then((data) => (transformFn ? transformFn(data) : data));
        } else {
            switch (res.status) {
                case 422:
                    return Promise.reject(new UserInputError());
                case 404:
                    return Promise.reject(new SearchError());
                case 500:
                    return Promise.reject(new BackendError());
                case 400:
                    return res.json().then((errResp) => {
                        // Narrow down input / data error
                        // this is useful to provide context to the user,
                        // but also to make datadog alerts less noisy.
                        if (errResp['error_type'] === 'OverbookingError') {
                            return Promise.reject(new OverbookingError(errResp.error_message));
                        } else if (errResp['error_type'] === 'ZeroCapacitiesError') {
                            return Promise.reject(new ZeroCapacityError(errResp.error_message));
                        } else {
                            const msg = errResp.error_message || errResp.detail;
                            return Promise.reject(new InputError(msg));
                        }
                    });
                default:
                    // These are unexpected responses - as in out of the api-contract defined above.
                    return res.text().then((respText) => Promise.reject(new CustomError(respText, res.status)));
            }
        }
    }
    let fetchFn = async () => fetch(queryUrl);
    if (auth) {
        fetchFn = buildAuthFetch(queryUrl, getTokenFunc);
    }
    return async () => fetchFn().then(parseAndTransform);
}

function useHttpPost(queryUrl: string, body: object, transformFn?: (param: unknown) => unknown, auth = true) {
    const getTokenFunc = useAuthToken();
    // TODO: refactor duplicate handling of error codes
    function parseAndTransform(res: Response) {
        if (res.ok) {
            return res.json().then((data) => (transformFn ? transformFn(data) : data));
        } else {
            switch (res.status) {
                case 422:
                    return Promise.reject(new UserInputError());
                case 404:
                    return Promise.reject(new SearchError());
                case 500:
                    return Promise.reject(new BackendError());
                case 400:
                    return Promise.reject(new InputError());
                default:
                    // These are unexpected responses - as in out of the api-contract defined above.
                    return res.text().then((respText) => Promise.reject(new CustomError(respText, res.status)));
            }
        }
    }
    let fetchFn = async () => fetch(queryUrl, { method: 'POST', body: JSON.stringify(body) });
    if (auth) {
        fetchFn = buildAuthFetch(queryUrl, getTokenFunc, 'POST', body);
    }
    return async () => fetchFn().then(parseAndTransform);
}

export function useRideSearch(rideId: string, auth = true) {
    const apiConfig = useApiConfig();
    const queryUrl = `${apiConfig.ridehubApiUrl}/v1/search?ride_uuid=${rideId}`;
    const queryFn = useHttpGet(queryUrl, undefined, auth);
    return useQuery<boolean, CustomError, RideSearchResult>({
        queryKey: [queryUrl],
        queryFn,
        enabled: !!rideId,
        refetchOnMount: false,
        retry: false,
        staleTime: Number.POSITIVE_INFINITY,
    });
}

export function useLineRidesSearch(
    lineCodes: Array<string>,
    departureStart: Temporal.PlainDate,
    tripNumberFilter: 'odd' | 'even' | 'even_and_odd' | 'exact',
    selectedWeekdays: string,
    departureEnd?: Temporal.PlainDate,
    tripNumber?: number,
    bidPriceActiveOnly = false,
    enabled = true,
    auth = true
) {
    const hasCorrectTNConfig = tripNumberFilter !== 'exact' || tripNumber !== undefined;
    const isEnabled = lineCodes.length > 0 && !!departureEnd && enabled && hasCorrectTNConfig;
    const apiConfig = useApiConfig();
    let queryUrl =
        `${apiConfig.ridehubApiUrl}/v2/search?` +
        lineCodes.map((code) => `line_codes=${encodeURIComponent(code)}`).join('&') +
        `&departure_start=${departureStart?.toString()}` +
        `&departure_end=${departureEnd?.toString()}` +
        `&weekdays=${selectedWeekdays}` +
        `&trip_number_filter=${tripNumberFilter}`;
    if (tripNumber !== undefined) {
        queryUrl += `&trip_number=${tripNumber}`;
    }
    if (bidPriceActiveOnly) {
        queryUrl += `&bid_price_active_only=true`;
    }
    const queryFn = useHttpGet(queryUrl, undefined, auth);
    return useQuery<boolean, CustomError, LineRidesSearchResult>({
        queryKey: [queryUrl],
        queryFn,
        enabled: isEnabled,
        refetchOnMount: false,
        retry: false,
        staleTime: Number.POSITIVE_INFINITY,
    });
}

export function useExchangeRates(enabled = true, auth = true) {
    const apiConfig = useApiConfig();
    const rmsBaseCurrency = 'EUR';
    const queryUrl = `${apiConfig.ridehubApiUrl}/v1/exchange-rates?source_currency=${rmsBaseCurrency}`;
    const queryFn = useHttpGet(queryUrl, undefined, auth);
    return useQuery<boolean, CustomError, Array<ExchangeRate>>({
        queryKey: [queryUrl],
        queryFn,
        refetchOnMount: false,
        retry: false,
        staleTime: 0,
        enabled,
    });
}

export function useLines(auth = true) {
    const apiConfig = useApiConfig();
    const queryUrl = `${apiConfig.ridehubApiUrl}/v1/lines`;
    const queryFn = useHttpGet(queryUrl, undefined, auth);
    return useQuery<boolean, CustomError, Array<Line>>({
        queryKey: [queryUrl],
        queryFn: queryFn,
        enabled: true,
        refetchOnMount: false,
        retry: false,
        staleTime: 1000 * 60 * 60, //ms*s*min
    });
}

export function useRide(rideId?: string, auth = true) {
    const apiConfig = useApiConfig();
    const queryUrl = `${apiConfig.ridehubApiUrl}/v1/rides/${rideId}`;
    const queryFn = useHttpGet(queryUrl, transformRideData, auth);
    return useQuery<boolean, CustomError, RideDetails>({
        queryKey: [queryUrl],
        queryFn: queryFn,
        enabled: !!rideId,
        refetchOnMount: false,
        retry: false,
        staleTime: 0,
    });
}

export function useRideSummarylist(rideIds: Array<string>, auth = true) {
    const apiConfig = useApiConfig();
    const queryUrl = `${apiConfig.ridehubApiUrl}/v1/batch/ride-kpis`;
    const payload = { ride_ids: rideIds };
    const queryFn = useHttpPost(queryUrl, payload, transformRideSummaryData, auth);
    return useQuery<boolean, CustomError, Array<RideSummary>>({
        queryKey: [queryUrl, rideIds.join('_')],
        queryFn: queryFn,
        enabled: rideIds.length > 0,
        refetchOnMount: false,
        retry: false,
        staleTime: 1000 * 60 * 5, //ms*s*min
    });
}

export function useFeatureFlags(enabled = true, auth = true) {
    const apiConfig = useApiConfig();
    const queryUrl = `${apiConfig.ridehubApiUrl}/v1/feature-flags`;
    const queryFn = useHttpGet(queryUrl, undefined, auth);
    return useQuery<boolean, CustomError, FeatureFlags>({
        queryKey: [queryUrl],
        queryFn: queryFn,
        enabled,
        refetchOnMount: false,
        retry: false,
        staleTime: 1000 * 60 * 60, //ms*s*min - Cache for 1 hour
    });
}

export { createMockServer } from './mock-server';
